import React, {useReducer} from 'react';
import {Button, createMuiTheme, ThemeProvider, Container, Grid, Typography} from '@material-ui/core';
import {green} from '@material-ui/core/colors';

import {useSelector, useDispatch} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Dependent from './Dependent';

import Loading from 'Shared/components/Loading';
import {Redirect} from 'react-router-dom';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {addDependents, updateDependentList} from 'ApplicationEntry/ducks/dependentLists';
import {PhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import * as DR from './DependentReducer';
import * as I from 'immutable';
import {Dependents, EmptyDependents, isValidDependents} from 'ApplicationEntry/models/Dependents';
import {MAX_DEPENDENTS} from 'ApplicationEntry/models/DependentLists';
import Dialog from 'Shared/components/Dialog/Dialog';
import {matchApplication, InvalidApplication, ValidApplication} from 'ApplicationEntry/models/Application';
import {InvalidModalApplication, ValidModalApplication} from 'ApplicationEntry/models/ModalApplication';
import {ZipCode} from 'ApplicationEntry/models/ZipCode';

const theme = createMuiTheme({
  palette: {
    primary: green,
  },
});

type DialogProps = {
  dialogOpen: boolean;
  dialogClose: () => void;
  dialogTitle: string;
  dialogSubTitle: string;
  dialogAdditional: string;
  dependentIndex: number;
  editDependent: boolean;
};

const DependentMaintenanceDialog = ({
  dialogOpen,
  dialogClose,
  dialogTitle,
  dialogSubTitle,
  dialogAdditional,
  dependentIndex,
  editDependent,
}: DialogProps) => {
  const initialState: DR.State = {members: I.List<Dependents>().push(EmptyDependents)};

  const [localState, dispatch] = useReducer(DR.reducer, initialState);

  const applicationState = useSelector((state: ReducerState) => state.application);
  const dispatchRedux = useDispatch();

  const application = (applicationState as InvalidModalApplication | ValidModalApplication).application as
    | InvalidApplication
    | ValidApplication;

  if (applicationState.key === 'LoadingModalApplication') {
    return <Loading />;
  } else if (applicationState.key === 'UndeterminedModalApplication') {
    return <Redirect to={ApplicationEntryRouteConstants.WorkQueue} />;
  }

  const dependentLists = matchApplication(application, {
    Valid: valid => valid.dependentLists,
    Invalid: invalid => invalid.dependentLists,
    Undetermined: () => undefined,
  });

  if (editDependent && localState.members.toArray()[0].key === 'UnvalidatedDependents') {
    dispatch(DR.editDependent(dependentLists!.dependentList.toArray()[dependentIndex]));
  }

  const handleSave = () => {
    editDependent
      ? dispatchRedux(updateDependentList(localState.members.toArray()[0], dependentIndex))
      : dispatchRedux(addDependents(localState.members));
    dispatch(DR.emptyDependent());
    dialogClose();
  };

  const handleCancel = () => {
    dispatch(DR.emptyDependent());
    dialogClose();
  };

  return (
    <Dialog fullScreen open={dialogOpen} onClose={handleCancel} title={dialogTitle}>
      <div className="dependents-section__dialog" id="dependents-section__dialog">
        <Container
          className="dependents-section__dialog-container"
          id="dependents-section__dialog-container"
          maxWidth={false}
        >
          <div className="dependents-section__dialog-container__tile-container">
            <Typography variant="subtitle1" className="dependents-section__dialog-container__tile-container-title">
              {dialogSubTitle}
            </Typography>
          </div>

          {localState.members.map((ele, i) => (
            <Dependent
              key={i}
              index={i}
              DependentIndex={dependentIndex}
              editDependent={editDependent}
              removeDependent={() => dispatch(DR.removeDependent(i))}
              changeFirstName={(value: string) => dispatch(DR.updateFirstName(i, value))}
              changeMiddleName={(value: string) => dispatch(DR.updateMiddleName(i, value))}
              changeLastName={(value: string) => dispatch(DR.updateLastName(i, value))}
              changeRelationship={(value: string) => dispatch(DR.updateRelationship(i, value))}
              changeDOB={(value: Date | '') => dispatch(DR.updateDOB(i, value))}
              changeSSN={(value: string) => dispatch(DR.updateSSN(i, value))}
              changeSex={(value: string) => dispatch(DR.updateGender(i, value))}
              changeAddress={(value: string) => dispatch(DR.updateAddress(i, value))}
              changeAddressLine2={(value: string) => dispatch(DR.updateAddressLine2(i, value))}
              changeCity={(value: string) => dispatch(DR.updateCity(i, value))}
              changeState={(value: string) => dispatch(DR.updateState(i, value))}
              changeZip={(value: ZipCode) => dispatch(DR.updateZip(i, value))}
              changePhone={(value: PhoneNumber) => dispatch(DR.updatePhone(i, value))}
              members={ele}
              dependentListLength={dependentLists!.dependentList.size}
            />
          ))}

          {!editDependent && dependentLists!.dependentList.size + localState.members.size < MAX_DEPENDENTS ? (
            <>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                className="dependents-section__additonal-dependent"
                id="dependents-section__additonal-dependent"
              >
                <Button variant="contained" color="primary" onClick={() => dispatch(DR.addDependent())}>
                  <Typography variant="h6">+</Typography>
                  {dialogAdditional}
                </Button>
              </Grid>
            </>
          ) : null}

          <div className="dependents-section__buttons-container">
            <Button
              variant="contained"
              color="inherit"
              onClick={handleCancel}
              className="dependents-section__buttons"
              id="dependents-section__buttons-cancel"
            >
              Cancel
            </Button>
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="primary"
                className="dependents-section__buttons"
                onClick={handleSave}
                id="dependents-section__buttons-save"
                disabled={localState.members.some(isValidDependents)}
              >
                Save Changes
              </Button>
            </ThemeProvider>
          </div>
        </Container>
      </div>
    </Dialog>
  );
};

export default DependentMaintenanceDialog;
